import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixRestaurantsV1AvailabilityException from './restaurants-v1-availability-exception-availability-exceptions.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface AvailabilityException {
  /**
   * Availability exception ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the availability exception is updated.
   * To prevent conflicting changes, the current revision must be specified when updating the availability exception.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  startTime?: Date | null;
  /** The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  endTime?: Date | null;
  /**
   * Whether the exception makes the [`start_time`, `end_time`] range available.
   * If `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.
   * Currently, only `false` is supported.
   * @readonly
   */
  available?: boolean | null;
  /**
   * Exception name.
   * @maxLength 500
   */
  name?: string | null;
  /** Fulfillment methods for which this exception applies. */
  affectedFulfillmentMethods?: AffectedFulfillmentMethods;
  /**
   * ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)
   * @format GUID
   */
  operationId?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /**
   * Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.
   * @format GUID
   * @readonly
   */
  businessLocationId?: string | null;
  /** Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions. */
  tags?: Tags;
}

export interface AffectedFulfillmentMethods
  extends AffectedFulfillmentMethodsAffectedMethodsOptionsOneOf {
  /** Settings for specific fulfillment methods. */
  specificFulfillmentMethodsOptions?: SpecificFulfillmentMethodsImpactScope;
  /** The type of the fulfillment methods this exception applies to. */
  affectedMethods?: AffectedMethodsWithLiterals;
}

/** @oneof */
export interface AffectedFulfillmentMethodsAffectedMethodsOptionsOneOf {
  /** Settings for specific fulfillment methods. */
  specificFulfillmentMethodsOptions?: SpecificFulfillmentMethodsImpactScope;
}

/** The scope the exception applied. */
export enum AffectedMethods {
  /** All fulfillment methods of this operation applied for this exception. */
  ALL_FULFILLMENT_METHODS = 'ALL_FULFILLMENT_METHODS',
  /** Specific fulfillment methods applied for this exception. */
  SPECIFIC_FULFILLMENT_METHODS = 'SPECIFIC_FULFILLMENT_METHODS',
  /** All pickup fulfillment methods applied for this exception. */
  ALL_PICKUP_FULFILLMENT_METHODS = 'ALL_PICKUP_FULFILLMENT_METHODS',
  /** All delivery fulfillment methods applied for this exception. */
  ALL_DELIVERY_FULFILLMENT_METHODS = 'ALL_DELIVERY_FULFILLMENT_METHODS',
}

/** @enumType */
export type AffectedMethodsWithLiterals =
  | AffectedMethods
  | 'ALL_FULFILLMENT_METHODS'
  | 'SPECIFIC_FULFILLMENT_METHODS'
  | 'ALL_PICKUP_FULFILLMENT_METHODS'
  | 'ALL_DELIVERY_FULFILLMENT_METHODS';

/** Details of the specific fulfillment methods this exception applies to. */
export interface SpecificFulfillmentMethodsImpactScope {
  /**
   * IDs of the fulfillment methods this exception applies to.
   * @format GUID
   * @maxSize 500
   */
  fulfillmentMethodIds?: string[] | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export enum Type {
  /** Unspecified Availability exception type. */
  UNSPECIFIED_AVAILABILITY_EXCEPTION_TYPE = 'UNSPECIFIED_AVAILABILITY_EXCEPTION_TYPE',
  /** Ordering paused. */
  ORDERING_PAUSED = 'ORDERING_PAUSED',
  /** The restaurant may be closed completely or only partially. For example, a restaurant may be closed only for pickup orders, only for delivery orders, or only for orders from a specific delivery area. */
  CLOSURE = 'CLOSURE',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNSPECIFIED_AVAILABILITY_EXCEPTION_TYPE'
  | 'ORDERING_PAUSED'
  | 'CLOSURE';

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface CreateAvailabilityExceptionRequest {
  /** Availability exception details. */
  availabilityException: AvailabilityException;
}

export interface CreateAvailabilityExceptionResponse {
  /** The created availability exception. */
  availabilityException?: AvailabilityException;
}

export interface GetAvailabilityExceptionRequest {
  /**
   * ID of the availability exception to retrieve.
   * @format GUID
   */
  availabilityExceptionId: string;
}

export interface GetAvailabilityExceptionResponse {
  /** The requested availability exception. */
  availabilityException?: AvailabilityException;
}

export interface UpdateAvailabilityExceptionRequest {
  /** Availability exception to update. */
  availabilityException: AvailabilityException;
}

export interface UpdateAvailabilityExceptionResponse {
  /** Updated availability exception. */
  availabilityException?: AvailabilityException;
}

export interface DeleteAvailabilityExceptionRequest {
  /**
   * ID of the availability exception to delete.
   * @format GUID
   */
  availabilityExceptionId: string;
}

export interface DeleteAvailabilityExceptionResponse {}

export interface QueryAvailabilityExceptionsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 4
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 64
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryAvailabilityExceptionsResponse {
  /** List of availability exceptions. */
  availabilityExceptions?: AvailabilityException[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor pointing to next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkCreateAvailabilityExceptionsRequest {
  /**
   * Availability exception details.
   * @minSize 1
   * @maxSize 20
   */
  availabilityExceptions: AvailabilityException[];
  /** Whether the full availability exception entity is returned. Defaults to `true`. */
  returnEntity?: boolean;
}

export interface BulkCreateAvailabilityExceptionsResponse {
  /** Information about the created availability exception. */
  results?: BulkCreateAvailabilityExceptionsResult[];
  /** Metadata for the API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCreateAvailabilityExceptionsResult {
  /** Metadata for availability exception creation. */
  itemMetadata?: ItemMetadata;
  /** Created availability exception. */
  availabilityException?: AvailabilityException;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateAvailabilityExceptionsRequest {
  /**
   * Availability exceptions entities to update.
   * @minSize 1
   * @maxSize 100
   */
  availabilityExceptions: MaskedAvailabilityException[];
  /** Whether to receive the entity in the response. */
  returnEntity?: boolean;
}

export interface MaskedAvailabilityException {
  /** Availability exception entity to update. */
  availabilityException?: AvailabilityException;
  /** Explicit list of fields to update. */
  mask?: string[];
}

export interface BulkUpdateAvailabilityExceptionsResponse {
  /** Information about the updated availability exceptions. */
  results?: BulkAvailabilityExceptionResult[];
  /** Bulk Update Availability Exceptions metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkAvailabilityExceptionResult {
  /** Metadata of the availability exception update. */
  itemMetadata?: ItemMetadata;
  /** Availability exception (present when return_entity is true). */
  item?: AvailabilityException;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface BulkUpdateAvailabilityExceptionTagsRequest {
  /**
   * IDs of the availability exceptions to update tags for.
   * @format GUID
   * @maxSize 100
   */
  availabilityExceptionIds: string[];
  /** Tags to assign to the availability exceptions. */
  assignTags?: Tags;
  /** Tags to unassign from the availability exceptions. */
  unassignTags?: Tags;
}

export interface BulkUpdateAvailabilityExceptionTagsResponse {
  /**
   * Results of the bulk update.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateAvailabilityExceptionTagsResult[];
  /** Metadata for the bulk update. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateAvailabilityExceptionTagsResult {
  /** Metadata for the updated availability exception. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateAvailabilityExceptionTagsByFilterRequest {
  /** Filter that determines which availability exceptions to update tags for. */
  filter: Record<string, any> | null;
  /** Tags to assign to the availability exceptions. */
  assignTags?: Tags;
  /** Tags to unassign from the availability exceptions. */
  unassignTags?: Tags;
}

export interface BulkUpdateAvailabilityExceptionTagsByFilterResponse {
  /**
   * Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status.
   * @format GUID
   */
  jobId?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type BulkUpdateAvailabilityExceptionTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateAvailabilityExceptionTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface AvailabilityExceptionCreatedEnvelope {
  entity: AvailabilityException;
  metadata: EventMetadata;
}

/**
 * Triggered when an availability exception is created.
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.AVAILABILITY_EXCEPTION_READ
 * @webhook
 * @eventType wix.restaurants.v1.availability_exception_created
 * @slug created
 */
export declare function onAvailabilityExceptionCreated(
  handler: (event: AvailabilityExceptionCreatedEnvelope) => void | Promise<void>
): void;

export interface AvailabilityExceptionDeletedEnvelope {
  entity: AvailabilityException;
  metadata: EventMetadata;
}

/**
 * Triggered when an availability exception is deleted.
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.AVAILABILITY_EXCEPTION_READ
 * @webhook
 * @eventType wix.restaurants.v1.availability_exception_deleted
 * @serviceIdentifier wix.restaurants.v1.AvailabilityExceptionsService
 * @slug deleted
 */
export declare function onAvailabilityExceptionDeleted(
  handler: (event: AvailabilityExceptionDeletedEnvelope) => void | Promise<void>
): void;

export interface AvailabilityExceptionUpdatedEnvelope {
  entity: AvailabilityException;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when an availability exception is updated.
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.AVAILABILITY_EXCEPTION_READ
 * @webhook
 * @eventType wix.restaurants.v1.availability_exception_updated
 * @slug updated
 */
export declare function onAvailabilityExceptionUpdated(
  handler: (event: AvailabilityExceptionUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a new availability exception.
 * @param availabilityException - Availability exception details.
 * @public
 * @requiredField availabilityException
 * @requiredField availabilityException.affectedFulfillmentMethods
 * @requiredField availabilityException.affectedFulfillmentMethods.affectedMethods
 * @requiredField availabilityException.available
 * @requiredField availabilityException.endTime
 * @requiredField availabilityException.operationId
 * @requiredField availabilityException.startTime
 * @permissionId RESTAURANTS.AVAILABILITY_EXCEPTION_CREATE
 * @applicableIdentity APP
 * @returns The created availability exception.
 * @fqn wix.restaurants.v1.AvailabilityExceptionsService.CreateAvailabilityException
 */
export async function createAvailabilityException(
  availabilityException: NonNullablePaths<
    AvailabilityException,
    | `affectedFulfillmentMethods`
    | `affectedFulfillmentMethods.affectedMethods`
    | `available`
    | `endTime`
    | `operationId`
    | `startTime`,
    3
  >
): Promise<
  NonNullablePaths<
    AvailabilityException,
    | `affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds`
    | `affectedFulfillmentMethods.affectedMethods`
    | `tags.privateTags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    availabilityException: availabilityException,
  });

  const reqOpts =
    ambassadorWixRestaurantsV1AvailabilityException.createAvailabilityException(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.availabilityException!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { availabilityException: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['availabilityException']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves an availability exception.
 * @param availabilityExceptionId - ID of the availability exception to retrieve.
 * @public
 * @requiredField availabilityExceptionId
 * @permissionId RESTAURANTS.AVAILABILITY_EXCEPTION_READ
 * @applicableIdentity APP
 * @returns The requested availability exception.
 * @fqn wix.restaurants.v1.AvailabilityExceptionsService.GetAvailabilityException
 */
export async function getAvailabilityException(
  availabilityExceptionId: string
): Promise<
  NonNullablePaths<
    AvailabilityException,
    | `affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds`
    | `affectedFulfillmentMethods.affectedMethods`
    | `tags.privateTags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    availabilityExceptionId: availabilityExceptionId,
  });

  const reqOpts =
    ambassadorWixRestaurantsV1AvailabilityException.getAvailabilityException(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.availabilityException!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { availabilityExceptionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['availabilityExceptionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates an availability exception.
 *
 * Each time the availability exception is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the availability exception.
 * This ensures you're working with the latest availability exception and prevents unintended overwrites.
 * @param _id - Availability exception ID.
 * @public
 * @requiredField _id
 * @requiredField availabilityException
 * @requiredField availabilityException.revision
 * @permissionId RESTAURANTS.AVAILABILITY_EXCEPTION_UPDATE
 * @applicableIdentity APP
 * @returns Updated availability exception.
 * @fqn wix.restaurants.v1.AvailabilityExceptionsService.UpdateAvailabilityException
 */
export async function updateAvailabilityException(
  _id: string,
  availabilityException: NonNullablePaths<
    UpdateAvailabilityException,
    `revision`,
    2
  >
): Promise<
  NonNullablePaths<
    AvailabilityException,
    | `affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds`
    | `affectedFulfillmentMethods.affectedMethods`
    | `tags.privateTags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    availabilityException: { ...availabilityException, id: _id },
  });

  const reqOpts =
    ambassadorWixRestaurantsV1AvailabilityException.updateAvailabilityException(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.availabilityException!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { availabilityException: '$[1]' },
        explicitPathsToArguments: { 'availabilityException.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'availabilityException']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateAvailabilityException {
  /**
   * Availability exception ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the availability exception is updated.
   * To prevent conflicting changes, the current revision must be specified when updating the availability exception.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  startTime?: Date | null;
  /** The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  endTime?: Date | null;
  /**
   * Whether the exception makes the [`start_time`, `end_time`] range available.
   * If `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.
   * Currently, only `false` is supported.
   * @readonly
   */
  available?: boolean | null;
  /**
   * Exception name.
   * @maxLength 500
   */
  name?: string | null;
  /** Fulfillment methods for which this exception applies. */
  affectedFulfillmentMethods?: AffectedFulfillmentMethods;
  /**
   * ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)
   * @format GUID
   */
  operationId?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /**
   * Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.
   * @format GUID
   * @readonly
   */
  businessLocationId?: string | null;
  /** Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions. */
  tags?: Tags;
}

/**
 * Deletes an availability exception.
 * @param availabilityExceptionId - ID of the availability exception to delete.
 * @public
 * @requiredField availabilityExceptionId
 * @permissionId RESTAURANTS.AVAILABILITY_EXCEPTION_DELETE
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.AvailabilityExceptionsService.DeleteAvailabilityException
 */
export async function deleteAvailabilityException(
  availabilityExceptionId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    availabilityExceptionId: availabilityExceptionId,
  });

  const reqOpts =
    ambassadorWixRestaurantsV1AvailabilityException.deleteAvailabilityException(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { availabilityExceptionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['availabilityExceptionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of availability exceptions.
 *
 * The `queryAvailabilityExceptions()` function builds a query to retrieve a list of availability exceptions and returns an `AvailabilityExceptionsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/availability-exceptions/availability-exceptions-query-builder/find) function.
 *
 * You can refine the query by chaining `AvailabilityExceptionsQueryBuilder` functions onto the query. `AvailabilityExceptionsQueryBuilder` functions enable you to filter, sort, and control the results that `queryAvailabilityExceptions()` returns.
 *
 * `queryAvailabilityExceptions()` runs with the following `AvailabilityExceptionsQueryBuilder` defaults, which you can override:
 *
 * - [`limit(50)`](/availability-exceptions/availability-exceptions-query-builder/limit)
 * - [`ascending('_id')`](/availability-exceptions/availability-exceptions-query-builder/ascending)
 *
 * The following `AvailabilityExceptionsQueryBuilder` functions are supported for `queryAvailabilityExceptions()`. For a full description of the availability exception object, see the object returned for the [`items`](/availability-exceptions/availability-exceptions-query-result/items) property in `AvailabilityExceptionsQueryResult`.
 * @public
 * @permissionId RESTAURANTS.AVAILABILITY_EXCEPTION_READ
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.AvailabilityExceptionsService.QueryAvailabilityExceptions
 */
export function queryAvailabilityExceptions(): AvailabilityExceptionsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    AvailabilityException,
    'CURSOR',
    QueryAvailabilityExceptionsRequest,
    QueryAvailabilityExceptionsResponse
  >({
    func: async (payload: QueryAvailabilityExceptionsRequest) => {
      const reqOpts =
        ambassadorWixRestaurantsV1AvailabilityException.queryAvailabilityExceptions(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (
      query: QueryAvailabilityExceptionsRequest['query']
    ) => {
      const args = [query, {}] as [
        QueryAvailabilityExceptionsRequest['query'],
        {}
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryAvailabilityExceptionsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.availabilityExceptions,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface AvailabilityExceptionsQueryResult extends QueryCursorResult {
  items: AvailabilityException[];
  query: AvailabilityExceptionsQueryBuilder;
  next: () => Promise<AvailabilityExceptionsQueryResult>;
  prev: () => Promise<AvailabilityExceptionsQueryResult>;
}

export interface AvailabilityExceptionsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'startTime'
      | 'endTime'
      | 'available'
      | 'name'
      | 'affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds'
      | 'affectedFulfillmentMethods.affectedMethods',
    value: any
  ) => AvailabilityExceptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'startTime'
      | 'endTime'
      | 'available'
      | 'name'
      | 'affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds'
      | 'affectedFulfillmentMethods.affectedMethods',
    value: any
  ) => AvailabilityExceptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'startTime'
      | 'endTime'
      | 'name',
    value: any
  ) => AvailabilityExceptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'startTime'
      | 'endTime'
      | 'name',
    value: any
  ) => AvailabilityExceptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'startTime'
      | 'endTime'
      | 'name',
    value: any
  ) => AvailabilityExceptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'startTime'
      | 'endTime'
      | 'name',
    value: any
  ) => AvailabilityExceptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'name',
    value: string
  ) => AvailabilityExceptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'startTime'
      | 'endTime'
      | 'available'
      | 'name'
      | 'affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds'
      | 'affectedFulfillmentMethods.affectedMethods',
    value: any[]
  ) => AvailabilityExceptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasAll: (
    propertyName: 'affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds',
    value: any[]
  ) => AvailabilityExceptionsQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'startTime'
      | 'endTime'
      | 'available'
      | 'name'
      | 'affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds'
      | 'affectedFulfillmentMethods.affectedMethods',
    value: any
  ) => AvailabilityExceptionsQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'startTime'
      | 'endTime'
      | 'available'
      | 'name'
      | 'affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds'
      | 'affectedFulfillmentMethods.affectedMethods',
    value: boolean
  ) => AvailabilityExceptionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'startTime'
      | 'endTime'
      | 'available'
      | 'name'
      | 'affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds'
      | 'affectedFulfillmentMethods.affectedMethods'
    >
  ) => AvailabilityExceptionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'startTime'
      | 'endTime'
      | 'available'
      | 'name'
      | 'affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds'
      | 'affectedFulfillmentMethods.affectedMethods'
    >
  ) => AvailabilityExceptionsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => AvailabilityExceptionsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => AvailabilityExceptionsQueryBuilder;
  find: () => Promise<AvailabilityExceptionsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.restaurants.v1.AvailabilityExceptionsService.QueryAvailabilityExceptions
 * @requiredField query
 */
export async function typedQueryAvailabilityExceptions(
  query: AvailabilityExceptionQuery
): Promise<
  NonNullablePaths<
    QueryAvailabilityExceptionsResponse,
    | `availabilityExceptions`
    | `availabilityExceptions.${number}.affectedFulfillmentMethods.affectedMethods`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixRestaurantsV1AvailabilityException.queryAvailabilityExceptions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AvailabilityExceptionQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        'affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds'
      ];
      operators: ['$hasAll', '$hasSome'];
      sort: 'BOTH';
    },
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'affectedFulfillmentMethods.affectedMethods',
        'available',
        'endTime',
        'name',
        'startTime'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  AvailabilityException,
  AvailabilityExceptionQuerySpec
>;
export type AvailabilityExceptionQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Number of items to load. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 4 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 64 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      AvailabilityException,
      AvailabilityExceptionQuerySpec,
      AvailabilityExceptionQuery
    >(),
  },
};

/**
 * Creates multiple availability exceptions.
 * @param availabilityExceptions - Availability exception details.
 * @public
 * @requiredField availabilityExceptions
 * @requiredField availabilityExceptions.affectedFulfillmentMethods
 * @requiredField availabilityExceptions.affectedFulfillmentMethods.affectedMethods
 * @requiredField availabilityExceptions.available
 * @requiredField availabilityExceptions.endTime
 * @requiredField availabilityExceptions.operationId
 * @requiredField availabilityExceptions.startTime
 * @permissionId RESTAURANTS.AVAILABILITY_EXCEPTION_CREATE
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.AvailabilityExceptionsService.BulkCreateAvailabilityExceptions
 */
export async function bulkCreateAvailabilityExceptions(
  availabilityExceptions: NonNullablePaths<
    AvailabilityException,
    | `affectedFulfillmentMethods`
    | `affectedFulfillmentMethods.affectedMethods`
    | `available`
    | `endTime`
    | `operationId`
    | `startTime`,
    3
  >[],
  options?: BulkCreateAvailabilityExceptionsOptions
): Promise<
  NonNullablePaths<
    BulkCreateAvailabilityExceptionsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.availabilityException.affectedFulfillmentMethods.affectedMethods`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    availabilityExceptions: availabilityExceptions,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixRestaurantsV1AvailabilityException.bulkCreateAvailabilityExceptions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          availabilityExceptions: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['availabilityExceptions', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateAvailabilityExceptionsOptions {
  /** Whether the full availability exception entity is returned. Defaults to `true`. */
  returnEntity?: boolean;
}

/**
 * Updates multiple availability exceptions.
 * @param availabilityExceptions - Availability exceptions entities to update.
 * @public
 * @requiredField availabilityExceptions
 * @requiredField availabilityExceptions.availabilityException._id
 * @requiredField availabilityExceptions.availabilityException.revision
 * @permissionId RESTAURANTS.AVAILABILITY_EXCEPTION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.AvailabilityExceptionsService.BulkUpdateAvailabilityExceptions
 */
export async function bulkUpdateAvailabilityExceptions(
  availabilityExceptions: NonNullablePaths<
    MaskedAvailabilityException,
    `availabilityException._id` | `availabilityException.revision`,
    3
  >[],
  options?: BulkUpdateAvailabilityExceptionsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateAvailabilityExceptionsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.affectedFulfillmentMethods.affectedMethods`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    availabilityExceptions: availabilityExceptions,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixRestaurantsV1AvailabilityException.bulkUpdateAvailabilityExceptions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          availabilityExceptions: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['availabilityExceptions', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateAvailabilityExceptionsOptions {
  /** Whether to receive the entity in the response. */
  returnEntity?: boolean;
}

/**
 * Synchronously update tags on multiple availability exceptions.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 * @param availabilityExceptionIds - IDs of the availability exceptions to update tags for.
 * @public
 * @requiredField availabilityExceptionIds
 * @permissionId RESTAURANTS.AVAILABILITY_EXCEPTION_UPDATE_TAGS
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.AvailabilityExceptionsService.BulkUpdateAvailabilityExceptionTags
 */
export async function bulkUpdateAvailabilityExceptionTags(
  availabilityExceptionIds: string[],
  options?: BulkUpdateAvailabilityExceptionTagsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateAvailabilityExceptionTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkUpdateAvailabilityExceptionTagsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    availabilityExceptionIds: availabilityExceptionIds,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixRestaurantsV1AvailabilityException.bulkUpdateAvailabilityExceptionTags(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          availabilityExceptionIds: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['availabilityExceptionIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateAvailabilityExceptionTagsOptions {
  /** Tags to assign to the availability exceptions. */
  assignTags?: Tags;
  /** Tags to unassign from the availability exceptions. */
  unassignTags?: Tags;
}

/**
 * Asynchronously update tags on multiple availability exceptions according to the specified filter.
 * If a filter isn't specified, this method updates all availability exceptions.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 * @param filter - Filter that determines which availability exceptions to update tags for.
 * @public
 * @requiredField filter
 * @permissionId RESTAURANTS.AVAILABILITY_EXCEPTION_UPDATE_TAGS
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.AvailabilityExceptionsService.BulkUpdateAvailabilityExceptionTagsByFilter
 */
export async function bulkUpdateAvailabilityExceptionTagsByFilter(
  filter: Record<string, any>,
  options?: BulkUpdateAvailabilityExceptionTagsByFilterOptions
): Promise<
  NonNullablePaths<
    BulkUpdateAvailabilityExceptionTagsByFilterResponse,
    `jobId`,
    2
  > & {
    __applicationErrorsType?: BulkUpdateAvailabilityExceptionTagsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixRestaurantsV1AvailabilityException.bulkUpdateAvailabilityExceptionTagsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateAvailabilityExceptionTagsByFilterOptions {
  /** Tags to assign to the availability exceptions. */
  assignTags?: Tags;
  /** Tags to unassign from the availability exceptions. */
  unassignTags?: Tags;
}
